import Image from 'next/image'
import Link from 'next/link'
import { PUBLIC_LANDING_ROUTES, UrlConfig } from '@/setup/routing/routing'
import { ALL_SOLUTION_ITEMS } from '@/features/landing-page/helpers/landing-solutions'

interface IFooterLink {
    label: string
    href: string
    external?: boolean
}

interface IFooterColumn {
    title: string
    links: IFooterLink[]
}

/*
 * Sections + links are kept identical to the current (old) footer for now — to be slimmed to the
 * new design's set later. Because nothing is dropped here, no page loses its footer link.
 */
const ABOUT: IFooterLink[] = [
    { label: 'Pricing', href: PUBLIC_LANDING_ROUTES.PRICING },
    { label: 'Case Studies', href: PUBLIC_LANDING_ROUTES.CASE_STUDY },
    { label: 'News', href: PUBLIC_LANDING_ROUTES.NEWS },
    { label: 'Learn', href: PUBLIC_LANDING_ROUTES.LEARN },
    { label: 'Careers', href: 'https://superscale.notion.site/careers-jobs', external: true },
    { label: 'Affiliate Program', href: '/affiliate/partners' },
    { label: 'Status', href: 'https://status.superscale.ai/', external: true },
]

const SOLUTIONS: IFooterLink[] = ALL_SOLUTION_ITEMS.map((item) => ({ label: item.title, href: item.href }))

const CASE_STUDIES: IFooterLink[] = [
    { label: 'All Case Studies', href: PUBLIC_LANDING_ROUTES.CASE_STUDY },
    { label: 'Sumup', href: '/case-study/sumup' },
    { label: 'Taxfix', href: '/case-study/taxfix' },
    { label: 'Advercy', href: '/case-study/advercy' },
    { label: 'Stromnow', href: '/case-study/stromnow' },
    { label: 'Twineo', href: '/case-study/twineo' },
    { label: 'Lila', href: '/case-study/lila' },
    { label: 'Ascend Bible', href: '/case-study/ascend-bible' },
    { label: 'Was Kann Ich Essen', href: '/case-study/was-kann-ich-essen' },
    { label: 'Marketbirds', href: '/case-study/marketbirds' },
]

const COMPARE: IFooterLink[] = [
    { label: 'Best AI marketing agents', href: '/compare/the-best-ai-marketing-agents' },
    { label: 'Best agentic platforms', href: '/compare/best-agentic-marketing-platforms' },
    { label: 'Best AI media buying', href: '/compare/best-ai-media-buying-tools-meta' },
    { label: 'vs Arcads', href: '/compare/superscale-vs-arcads' },
    { label: 'vs Creatify', href: '/compare/superscale-vs-creatify' },
    { label: 'vs HeyGen', href: '/compare/superscale-vs-heygen' },
    { label: 'vs Icon', href: '/compare/superscale-vs-icon' },
    { label: 'vs Laria', href: '/compare/superscale-vs-laria' },
    { label: 'vs MakeUGC', href: '/compare/superscale-vs-makeugc' },
    { label: 'vs Reelfarm', href: '/compare/superscale-vs-reelfarm' },
]

const LEGALS: IFooterLink[] = [
    { label: 'Imprint', href: PUBLIC_LANDING_ROUTES.IMPRINT },
    { label: 'Privacy Policy', href: PUBLIC_LANDING_ROUTES.PRIVACY_POLICY },
    { label: 'Terms & Conditions', href: PUBLIC_LANDING_ROUTES.TERMS_AND_CONDITIONS },
    { label: 'Affiliate Terms', href: '/affiliate/terms' },
    { label: 'Contact', href: 'mailto:support@superscale.ai', external: true },
]

const SOCIALS: IFooterLink[] = [
    { label: 'X', href: 'https://x.com/superscale_ai', external: true },
    { label: 'LinkedIn', href: 'https://www.linkedin.com/company/superscale-ai', external: true },
    { label: 'Instagram', href: 'https://www.instagram.com/getsuperscaleai', external: true },
]

const COLUMNS: IFooterColumn[] = [
    { title: 'About us', links: ABOUT },
    { title: 'Solutions', links: SOLUTIONS },
    { title: 'Case Studies', links: CASE_STUDIES },
    { title: 'Compare', links: COMPARE },
    { title: 'Legals', links: LEGALS },
]

const LINK_CLASS = 'text-[16px] leading-[1.1] text-[#FDFBF8] transition-opacity hover:opacity-70'

function FooterLink({ link }: { link: IFooterLink }) {
    if (link.external) {
        return (
            <a href={link.href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
                {link.label}
            </a>
        )
    }
    return (
        <Link href={link.href} prefetch={false} className={LINK_CLASS}>
            {link.label}
        </Link>
    )
}

/**
 * lp-v3 footer — SERVER component (all links ship in the static HTML). Orange block, cream text,
 * link columns on top, superscale logo bottom-left and socials bottom-right.
 */
export function LpFooter() {
    return (
        <footer className="relative z-10 bg-[#FF5500]">
            <div className="flex flex-col gap-16 p-6 md:gap-24 md:p-[60px]">
                <div className="grid grid-cols-2 gap-x-8 gap-y-10 md:grid-cols-3 lg:grid-cols-5">
                    {COLUMNS.map((column) => (
                        <div key={column.title} className="flex flex-col gap-3">
                            <div className="text-[14px] leading-[1.1] text-[#FDFBF8]/55">{column.title}</div>
                            <ul className="flex flex-col gap-2.5">
                                {column.links.map((link) => (
                                    <li key={`${link.label}-${link.href}`}>
                                        <FooterLink link={link} />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="flex flex-col-reverse gap-4 md:flex-row md:items-end md:justify-between">
                    <Link
                        href={UrlConfig['/'].getUrl()}
                        aria-label="Superscale home"
                        className="block w-full md:w-fit md:shrink-0"
                    >
                        <Image
                            src="/assets/landing-v3/superscale-logo-with-text.svg"
                            alt="Superscale"
                            width={149}
                            height={32}
                            className="h-auto w-full brightness-0 invert md:h-[72px] md:w-auto"
                        />
                    </Link>

                    <div className="flex items-center justify-between gap-6 md:justify-end">
                        {SOCIALS.map((social) => (
                            <a
                                key={social.label}
                                href={social.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={LINK_CLASS}
                            >
                                {social.label}
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        </footer>
    )
}
